import { useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "../../AiChat/components";

// Questions are written in both languages and follow the language toggle, so a
// click sends the question in the language the answer will come back in.
const SAMPLE_QUESTIONS = [
  {
    category: { en: "Product spec", zh: "產品規格" },
    text: {
      en: "show me the spec of tg4040",
      zh: "TG4040 的規格是什麼",
    },
  },
  {
    category: { en: "Single attribute", zh: "單一屬性" },
    text: {
      en: "what is K of tg4040",
      zh: "TG4040 的導熱係數是多少",
    },
  },
  {
    category: { en: "Application", zh: "應用場景" },
    text: {
      en: "tell me the application of tg4040",
      zh: "TG4040 可以用在哪些應用",
    },
  },
  {
    category: { en: "Category ranking", zh: "類別找極值" },
    text: {
      en: "Which Thermal Putty has high thermal conductivity?",
      zh: "哪一款 Thermal Putty 的導熱係數最高？",
    },
  },
  {
    category: { en: "Category sorting", zh: "類別排序" },
    text: {
      en: "List Thermal Putty sorted by thermal conductivity in descending order",
      zh: "將 Thermal Putty 依導熱係數由高到低排列",
    },
  },
  {
    category: { en: "Competitor spec", zh: "競品規格" },
    text: {
      en: "show me the spec of HT3500",
      zh: "HT3500 的規格是什麼",
    },
  },
  {
    category: { en: "Tech Q&A", zh: "技術問答" },
    text: {
      en: "What is Vapor Chamber?",
      zh: "什麼是均溫板？",
    },
  },
  // Turns the video toggle on when picked — the question alone would run as a
  // normal text lookup and look like the videos were broken.
  {
    category: { en: "Video", zh: "影片" },
    text: {
      en: "How do I apply thermal grease?",
      zh: "導熱膏怎麼用",
    },
    video: true,
  },
];

export default function SampleQuestions({
  setInput,
  setIncludeVideo,
  language,
  onSelect,
}) {
  const [page, setPage] = useState(0);
  const pageCount = Math.ceil(SAMPLE_QUESTIONS.length / 4);
  return (
    <div className="w-full">
      <h4 className="text-black dark:text-white text-center font-bold text-lg mb-5">
        {language === "zh" ? "範例問題" : "Sample Questions"}
      </h4>
      <p className="text-black/70 dark:text-white/70 text-center text-sm mb-6">
        {language === "zh"
          ? "點擊任一問題即可開始"
          : "Click any question below to get started"}
      </p>
      <div className="relative flex items-center gap-2 md:gap-4">
        <button
          type="button"
          aria-label={language === "zh" ? "上一頁問題" : "Previous questions"}
          disabled={page === 0}
          onClick={() => setPage(page - 1)}
          className="p-1 md:p-2 rounded-lg text-black dark:text-white hover:bg-black/10 dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeftIcon />
        </button>
        <div className="flex-1 min-w-0 grid grid-cols-1 md:grid-cols-2 gap-4">
          {SAMPLE_QUESTIONS.slice(page * 4, page * 4 + 4).map((q) => {
            const text = q.text[language] ?? q.text.en;
            return (
              <button
                type="button"
                key={q.category.en}
                onClick={() => {
                  setInput(text);
                  // Only ever turns it on: leaving a user's own choice switched off
                  // because they picked a non-video sample would be surprising.
                  if (q.video) setIncludeVideo(true);
                  onSelect();
                }}
                className="group relative overflow-hidden text-left px-5 py-6 rounded-xl bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 border border-orange-400/30 transition-all duration-300 text-white shadow-lg hover:shadow-2xl hover:shadow-orange-500/50 motion-safe:hover:-translate-y-1"
              >
                <span
                  aria-hidden="true"
                  className="absolute top-0 right-0 w-20 h-20 bg-white/10 rounded-full -mr-10 -mt-10 motion-safe:group-hover:scale-150 transition-transform duration-500"
                />
                <span className="relative block text-xs font-semibold uppercase tracking-wide text-orange-100 mb-2">
                  {q.category[language] ?? q.category.en}
                  {q.video && (
                    <span className="ml-1 normal-case text-orange-100">
                      {language === "zh"
                        ? "· 會開啟影片搜尋"
                        : "· turns on video search"}
                    </span>
                  )}
                </span>
                <span className="relative block text-sm font-medium leading-relaxed text-white">
                  {text}
                </span>
              </button>
            );
          })}
        </div>
        <button
          type="button"
          aria-label={language === "zh" ? "下一頁問題" : "Next questions"}
          disabled={page === pageCount - 1}
          onClick={() => setPage(page + 1)}
          className="p-1 md:p-2 rounded-lg text-black dark:text-white hover:bg-black/10 dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRightIcon />
        </button>
      </div>
      <div className="flex justify-center mt-5" aria-live="polite">
        <span className="px-4 py-2 rounded-lg bg-orange-500/20 dark:bg-orange-500/30 border border-orange-500/40 text-sm font-semibold text-orange-600 dark:text-orange-400">
          {language === "zh"
            ? `第 ${page + 1} / ${pageCount} 頁`
            : `Page ${page + 1} / ${pageCount}`}
        </span>
      </div>
    </div>
  );
}
